import React, {useEffect, useState} from 'react';
import { useUpdateQuestionOrderMutation, useGetSurveyToastsQuery } from '../../../redux/survey/survey.endpoints';
import { defaultQuestion, Question, SurveyFull, SurveyToast } from '../../../redux/survey/survey.types';
import { OrderList, OrderListChangeEvent, OrderListPassThroughOptions } from "primereact/orderlist";
import './survey-summary-component.css';
import SurveySummaryQuestion from "./survey-summary-question.component";
import { useDispatch } from "react-redux";
import { showNotification } from "../../../redux/notification/notification.slice";
import { isEqual } from "lodash";


import {isQuestionList} from "../../../services/surveys/surveys.helpers";
import { ViewSurveyMode } from '../../../pages/view-survey/view-survey.types';
import AddToastBtn from '../../toasts/add-toast-btn';
import ToastEditDialog from '../../toasts/toast-edit-dialog';
import PencilIcon from "../../../assets/icons-redesign/pencil.svg?react";

interface QuestionSummaryProps {
  survey?: SurveyFull,
  mode: ViewSurveyMode;
}

const PASS_THROUGH_OPTIONS: OrderListPassThroughOptions = {
  container: { className: 'flex min-w-0 grow' },
  controls: { className: 'hidden' },
  list: { className: 'bg-transparent min-h-min w-full min-w-0 max-w-full grow' },
  item: { className: 'p-0 md:pr-1 bg-transparent w-full min-w-0 max-w-full flex flex-row grow mx-0' },
  root: { className: 'max-w-full grow' },
};

const QuestionSummary: React.FC<QuestionSummaryProps> = ({ survey, mode }) => {
  const dispatch = useDispatch();
  const [updateQuestionOrder] = useUpdateQuestionOrderMutation();
  const [questions, setQuestions] = useState(survey?.questions || [defaultQuestion]);
  const [toasts, setToasts] = useState<SurveyToast[]>([]);
  const [selectedToast, setSelectedToast] = useState<SurveyToast | null>(null);
  const [isEditDialogOpen, setEditDialogOpen] = useState(false);

  const { data: toastsData, refetch } = useGetSurveyToastsQuery({ ext_survey_id: survey?.extSurveyId || "" }, {
    skip: !survey?.extSurveyId,
  });

  useEffect(() => {
    if (toastsData) {
      setToasts(toastsData);
    }
  }, [toastsData]);

  useEffect(() => {
    if (!survey?.questions) {
      return;
    }
    const orderedQuestions = [...survey.questions].sort((a, b) => a.questionOrder - b.questionOrder);
    setQuestions(orderedQuestions);
  }, [survey]);

  const scrollToQuestion = (questionId: string) => {
    const questionElement = document.getElementById(`question-${questionId}`);
    if (questionElement) {
      questionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const renderQuestion = (q: Question) => (
    <div
      onClick={() => scrollToQuestion(q.id)}
      className="cursor-pointer w-full"
    >
      <SurveySummaryQuestion question={q} />
    </div>
  );
  const renderQuestionView = (q: Question, index: number) => (
    <div
      key={index}
      className={
        index + 1 === questions.length
          ? 'flex w-full items-center rounded-b-xl border-t border-gray-201 bg-white px-2 py-3 text-black'
          : 'flex w-full items-center border-t border-gray-201 bg-white px-2 py-3 text-black'
      }
    >
      <div className="mr-2 shrink-0 px-2 text-gray-401">
        {index + 1}
      </div>
      <div className="grow truncate text-start">
        {q.title}
      </div>
    </div>
  );

  const onQuestionListChange = (event: OrderListChangeEvent) => {
    if (!isQuestionList(event.value) || !survey) {
      return;
    }

    const previousIds = questions.map(({ id }) => id);
    const updatedIds = event.value.map(({ id }) => id);
    if (isEqual(previousIds, updatedIds)) {
      return;
    }

    updateQuestionOrder({ ext_survey_id: survey.extSurveyId, questionArray: updatedIds });
  };

  const onListDragStart = () => {
    const hasSorting = questions.some(q => q.answers.some(a => a.answerBranch !== null || a.isFinalQuestion));
    if (hasSorting) {
      dispatch(showNotification({ summary: "Sorting the questions will clear all answer branching within the survey 'Jump to' or 'End Survey'", title: "Warning", theme: "info" }));
    }
  };

  const handleToastClick = (toast: SurveyToast) => {
    setSelectedToast(toast);
    setEditDialogOpen(true);
  };

  const renderToastList = () => (
    <>
      <h2 className="my-3 text-2xl">
        Toasts
      </h2>
      <div className="my-4 rounded-xl bg-white shadow-md">
        {toasts.length > 0 ? (
          toasts.map((toast, index) => (
            <div
              key={toast.id}
              className='flex w-full items-center rounded-b-xl px-2 py-3 text-black cursor-pointer'
              onClick={() => handleToastClick(toast)}
            >
              <div className="mr-2 shrink-0 px-2 text-gray-401">
                {index + 1}
              </div>
              <div className="grow truncate text-start">
                {toast.name}
              </div>
              <PencilIcon
                fill="#50555C"
                className="mr-2"
              />
            </div>
          ))
        ) : (
          <div className="flex w-full items-center p-4 text-gray-401">
            No toasts created yet.
          </div>
        )}
        <div className="p-4 pt-0">
          <AddToastBtn
            extSurveyId={survey?.extSurveyId || ""}
            refetchToastData={refetch}
          />
        </div>
      </div>

      <ToastEditDialog
        isOpen={isEditDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        toast={selectedToast}
        onToastUpdated={() => {
          setSelectedToast(null);
          setEditDialogOpen(false);
          refetch();
        }}
      />
    </>
  );

  return (
    <>
      {mode === 'create' ? (
        <div>
          <div className="question-summary-list flex overflow-scroll">
            <OrderList
              dragdrop
              onDragStart={onListDragStart}
              dataKey="id"
              value={questions}
              pt={PASS_THROUGH_OPTIONS}
              itemTemplate={renderQuestion}
              onChange={onQuestionListChange}
            />
          </div>
          {renderToastList()}
        </div>
      )
        : (
          <div className="flex flex-col justify-start rounded-xl bg-white shadow-md">
            <div className="ml-4 py-3 text-gray-401">
              Questions
            </div>
            {questions.map((q, index) => renderQuestionView(q, index))}
          </div>
        )
      }

    </>
  );
};

export default QuestionSummary;
